import React, {useEffect, useState} from "react";
import {Button, Card, CardBody, CardHeader, CardSubtitle, CardTitle} from "reactstrap";
import {IExoReplacer} from "../../../../store/exo/types";
import FeatherIcon from "feather-icons-react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import usePermissions from "../../../../hooks/usePermissions";
import {ExoService} from "../../../../services/exo.service";
import ExoReplacerRow from "./ExoReplacerRow";

interface CardExoReplacerProps {
    replacer: IExoReplacer
    onEdit?: (id: number) => void;
    onAddReplacement?: (id: number) => void;
}

const CardExoReplacer: React.FC<CardExoReplacerProps> = ({
                                                             replacer,
                                                             onEdit,
                                                             onAddReplacement
                                                         }) => {
    const queryClient = useQueryClient();
    const {hasPermission} = usePermissions();

    const [removeApprove, setRemoveApprove] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoReplacers"]});
    }

    const {mutate: removeReplacer, isPending: isPendingRemove} = useMutation({
        mutationFn: (replacerId: number) => ExoService.removeReplacer(replacerId),
        onSuccess: onSuccess,
        onError: () => setRemoveApprove(false),
        mutationKey: ["keyRemoveExoReplacer"],
    });

    const handleEditClick = () => {
        if (onEdit) onEdit(replacer.id)
    }

    const handleAddReplacementClick = () => {
        if (onAddReplacement) onAddReplacement(replacer.id)
    }

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
            return;
        }

        setSureToRemove(false)
        setRemoveApprove(true)
        removeReplacer(replacer.id)
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const isPendingRemoveFull = (isPendingRemove || !!queryClient.isFetching({queryKey: ["keyExoReplacers"]})) && removeApprove;

    return (
        <Card className={isPendingRemoveFull ? "opacity-50 pe-none" : undefined}>
            <CardHeader className="p-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <CardSubtitle className="mb-1">
                            <small className="text-muted">
                                Replace
                            </small>
                        </CardSubtitle>
                        <CardTitle tag="h5" className="fs-5 m-0">
                            {"{"} {replacer.key} {"}"}
                        </CardTitle>
                    </div>

                    <div className="d-flex align-items-center gap-1">
                        {hasPermission('edit-echo-replacers') &&
                            <Button className="d-flex p-1" color="success" onClick={handleEditClick}
                                    disabled={!hasPermission('edit-echo-replacers')}>
                                <FeatherIcon icon="edit-2" size={16}/>
                            </Button>}

                        {hasPermission('delete-echo-replacers') &&
                            <Button className="d-flex p-1" color="danger" onClick={handleRemoveClick}
                                    disabled={!hasPermission('delete-echo-replacers')}>
                                {sureToRemove ?
                                    <FeatherIcon icon="check" size={16}/> :
                                    isPendingRemoveFull ?
                                        <i className="mdi mdi-loading mdi-spin font-size-16 lh-1"></i> :
                                        <FeatherIcon icon="trash-2" size={16}/>}
                            </Button>}
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-3">
                <div className="d-flex justify-content-end mb-2">
                    <Button color="light" size="sm" type="button" onClick={handleAddReplacementClick}>
                        <i className="mdi mdi-plus me-1"></i>
                        Add replacement
                    </Button>
                </div>
                {replacer.replacement.map((replacement) => (
                    <ExoReplacerRow
                        key={`${replacement.from}-${replacement.to}`}
                        replacement={replacement}
                        readOnly
                    />
                ))}
            </CardBody>
        </Card>
    )
};

export default CardExoReplacer;